import { fabric } from 'fabric'
import { ObjectType, PROPERTIES_TO_EXPORT } from '../common/constants'
import { loadImageFromURL } from './image-loader'
import isNaN from 'lodash/isNaN'
import './fabricjs-custom-filters/filter-flexblur'
import { nanoid } from 'nanoid'
import * as PIXI from 'pixi.js'
import { getFabricFontMetrics, isColorBright, normalize } from './utils'
import { lightTheme } from '@/customTheme'
import CanvasImageRenderer from './canvasImageRenderer'
import { CanvasLayerShadowEffect } from '@/interfaces/CanvasLayerShadowEffect'
import { CanvasLayerOutlineEffect } from '@/interfaces/CanvasLayerOutlineEffect'
import { isUndefined } from 'mathjs'
import { getFontSizeToMatchRect, measureLineInfoForFont, measureTextMetrics } from './font-loader'
import { MediaFontRepository } from '../objects/media-repository/media_font_repository'
import { Rectangle } from "@/shared/sync/mediaRepository/rectangle"
import { Size } from "@/shared/sync/mediaRepository/size"
import { Point } from "@/shared/sync/mediaRepository/point"
import { MediaImageType } from '@/shared/sync/mediaRepository/media_image_type'
import { MediaImageRepository } from '../objects/media-repository/media_image_repository'
import { MediaImageRepositoryProvider } from '../objects/media-repository/MediaImageRepositoryProvider'

const textAligns = ['left', 'center', 'right', 'justify', 'justify-center']
const textAlignsNSFormat = ['left', 'right', 'center']

const blendMap = {
  hardLightBlendMode: 'hard-light',
  softLightBlendMode: 'soft-light',

  '': 'normal',
  multiplyBlendMode: 'multiply',
  screenBlendMode: 'screen',
  overlayBlendMode: 'overlay',
  darkenBlendMode: 'darken',
  lightenBlendMode: 'lighten',
  colorDodgeBlendMode: 'color-dodge',
  colorBurnBlendMode: 'color-burn',
  differenceBlendMode: 'difference',
  exclusionBlendMode: 'exclusion',
}
class ObjectToFabric {
  
  async cloneImage(element: fabric.StaticImage) {
    return new Promise(function (resovle, reject) {
      element.clone(cloned => {
        resovle(cloned)
      })
    })
  }

  async run(item, options, inputMediaRepository?) {
    let object
    let mediaRepository = inputMediaRepository ?? MediaImageRepositoryProvider.getEditingProjectInstance()
    item.type = item.type ? item.type : item.itemType
    switch (item.type) {
      case ObjectType.STATIC_VECTOR:
        object = await this[ObjectType.STATIC_VECTOR](item, options, mediaRepository)
        break
      case ObjectType.STATIC_PATH:
        object = await this[ObjectType.STATIC_PATH](item, options, mediaRepository)
        break
      case ObjectType.BAZAART_STICKER:
      case ObjectType.BAZAART_IMAGE:
      case ObjectType.BAZAART_SHAP:
      case ObjectType.BAZAART_DRAWING:
      case ObjectType.STATIC_IMAGE:
        object = await this[ObjectType.BAZAART_STICKER](item, options, mediaRepository)
        break
      case ObjectType.BAZAART_TEXT:
      case ObjectType.STATIC_TEXT:
      case ObjectType.BAZAART_IMAGETEXT:
      case ObjectType.OPEN_TYPE_TEXT:
        object = await this[ObjectType.BAZAART_TEXT](item, options, mediaRepository)
        break
      case ObjectType.BAZAART_BG:
      case ObjectType.BACKGROUND_IMAGE:
        object = await this[ObjectType.BAZAART_BG](item, options, mediaRepository)
        break
      case ObjectType.BAZAART_OVERLAY:
        object = await this[ObjectType.BAZAART_OVERLAY](item, options, mediaRepository)
        break
      case ObjectType.GROUP:
        object = await this[ObjectType.GROUP](item, options, mediaRepository)
        break
      case ObjectType.BAZAART_IMAGE_FROM_TEXT:
        object = await this[ObjectType.BAZAART_IMAGE_FROM_TEXT](item, options, mediaRepository)
        break 
    }
    return object
  }

  [ObjectType.STATIC_IMAGE](item, options, mediaRepository) {
    return new Promise(async (resolve, reject) => {
      try {
        const baseOptions = this.getBaseOptions(item, options)
        const src = item.metadata.src
        const image: any = await loadImageFromURL(src)

        const { width, height } = baseOptions
        if (!width || !height) {
          baseOptions.width = image.width
          baseOptions.height = image.height
        }

        const element = new fabric.StaticImage(image, {
          ...baseOptions,
          cropX: item.metadata.cropX || 0,
          cropY: item.metadata.cropY || 0,
        })

        const { top, left } = element

        if (isNaN(top) || isNaN(left)) {
          element.set({
            top: options.top,
            left: options.left,
          })
          element.scaleToWidth(480)
        }
        resolve(element)
      } catch (err) {
        reject(err)
      }
    })
  }

  [ObjectType.BAZAART_SHAP](item, options, mediaRepository) {
    return new Promise(async (resolve, reject) => {
      try {
        let object = (await this[ObjectType.BAZAART_STICKER](item, options, mediaRepository)) as fabric.StaticShap
        // object = object as fabric.StaticImage
        // debugger;
        // object.type = fabric.StaticShap.type
        resolve(object)
      } catch (err) {
        reject(err)
      }
    })
  }

  rotateAroundPointInsideObject(object, offsetX, offsetY, rotationAngle) {
      var pivotPoint = new fabric.Point(offsetX, offsetY); // Custom pivot point coordinates
      // Get the object's current center point
      var objectCenter = object.getCenterPoint();

      // Calculate the vector from the pivot point to the object's center
      var vectorToCenter = objectCenter.subtract(pivotPoint);

      // Rotate this vector by the desired angle
      var radians = fabric.util.degreesToRadians(rotationAngle);
      var cos = Math.cos(radians);
      var sin = Math.sin(radians);

      var rotatedVectorX = vectorToCenter.x * cos - vectorToCenter.y * sin;
      var rotatedVectorY = vectorToCenter.x * sin + vectorToCenter.y * cos;
      var rotatedVector = new fabric.Point(rotatedVectorX, rotatedVectorY);

      // Calculate the new center point after rotation
      var newCenter = pivotPoint.add(rotatedVector);

      // Update the object's position and rotation
      object.set({
        left: newCenter.x,
        top: newCenter.y,
        angle: object.angle + rotationAngle
      });
  }


  [ObjectType.BAZAART_STICKER](item, options, mediaRepository) {    
    return new Promise(async (resolve, reject) => {
      try {
        let img: any = null
        const optionsAPI = this.getOptionsAPI(item,options)
        let latestImageUrl = await mediaRepository.getImage(
          item.bazaartGuid,
          item.layerAssetStateId,
          MediaImageType.latest
        )

        let thumbnailImageUrl = item.loadThumbnail === false ? null : await mediaRepository.getImage(
          item.bazaartGuid,
          item.layerAssetStateId,
          MediaImageType.thumbnail
        )

        // TODO: this is a temporary solution to differentiate between latest image loading of saved projects
        // and graphics image loading of new project as well as templates
        if (item.isLatest) {
          thumbnailImageUrl = null;
        }
        
        img = await loadImageFromURL(thumbnailImageUrl ? thumbnailImageUrl : latestImageUrl)
        
        const sizeOnCanvas = new Size(options.width, options.height);
        let shadowEffects = new CanvasLayerShadowEffect();
        let inputBounds = new Rectangle(0, 0, img.width, img.height);
        let shadowRect = shadowEffects.revertEffectBounds(item, sizeOnCanvas, false, inputBounds);
        let nonSymmetricOffset = new Point(shadowRect.x, shadowRect.y) ;
        
        let outlineEffects = new CanvasLayerOutlineEffect();
        let outlineRect = outlineEffects.revertEffectBounds(item, sizeOnCanvas, false, shadowRect);
        
        let imageTargetWidthOnCanvas = item.sizeOnCanvas.width * options.width
        let imageTargetHeightOnCanvas = (imageTargetWidthOnCanvas / outlineRect.width) * outlineRect.height

        let imageScaleX = item.scaleX ? item.scaleX : imageTargetWidthOnCanvas / outlineRect.width
        let imageScaleY = item.scaleY ? item.scaleY : imageTargetHeightOnCanvas / outlineRect.height
        
        let boundingBox = new Rectangle(
          item.boundingBox.x,
          item.boundingBox.y,
          item.boundingBox.width,
          item.boundingBox.height
        )
        
        let leftOpt = item.left ? item.left : options.left + options.width * item.centerPoint.x + nonSymmetricOffset.x * imageScaleX * (item.transformation?.horizontalFlip ? -1 : 1)
        let topOpt = item.top ? item.top : options.top + options.height * item.centerPoint.y + nonSymmetricOffset.y * imageScaleY * (item.transformation?.verticalFlip ? -1 : 1)

        if(item.offsetX !== undefined) {
          leftOpt += item.offsetX
        }
        if(item.offsetY !== undefined) {
          topOpt += item.offsetY
        }

        let baseOptions = {
          ...optionsAPI,
          top: topOpt,
          left: leftOpt,
          width: img.width,
          height: img.height,
          scaleX: imageScaleX,
          scaleY: imageScaleY,
          originX: 'center',
          originY: 'center',
          cropX: 0,
          cropY: 0,
          boundingBox: boundingBox,
          sizeOnCanvas: item.sizeOnCanvas,
          latest: item.latest,
          isLatest: item.isLatest,
          flipX: item.flipX !== null && item.flipX !== undefined ? item.flipX : item.transformation.horizontalFlip,
          flipY: item.flipY !== null && item.flipY !== undefined ? item.flipY : item.transformation.verticalFlip,
          mask: item.mask,
          effects_from_template: item.effects ?? {},
          adjustments_from_template: item.adjustments ?? {},
          effects: item.effects ?? {
            hasFillImage: false,
          },
          id: item.bazaartGuid,
          type: item.type,
          hasTransparency: item.hasTransparency !== undefined ? item.hasTransparency : true,
          opacity: item.opacity ? item.opacity : 1,
          filters: item.filters ? item.filters : [],
          zoomX: item.zoomX ? item.zoomX : undefined,
          zoomY: item.zoomY ? item.zoomY : undefined,
          globalCompositeOperation: item.globalCompositeOperation ? item.globalCompositeOperation : undefined,
          selected: false,
          evented: true,
          useBzrtBgMask:item.useBzrtBgMask,
          layerAssetStateId: item.layerAssetStateId,
          transformation: {
            horizontalFlip:item.transformation.horizontalFlip,
            verticalFlip:item.transformation.verticalFlip,
            kDistort:item?.transformation?.kDistort,
          },
          centeredRotation: true,
          fullImgUrl:item?.fullImgUrl,
          hasPeople:item?.hasPeople,
          shouldUseMattedImage:item?.shouldUseMattedImage,
          sourceGraphicsItemName:item?.sourceGraphicsItemName,
          sourceGraphicsPackName:item?.sourceGraphicsPackName,
          sourceGraphicsType:item?.sourceGraphicsType,
          features:item?.features,
          videoSpeed:item?.videoSpeed,
          saliencyResults:item?.saliencyResults,
          isTemplateLayer: item.isTemplateLayer,
          filter:item.filter,
          filterIntensity:item.filterIntensity ? item.filterIntensity : 1,
          isStockImage: item.isStockImage,
          isMagicBackgroundLayer: item?.isMagicBackgroundLayer,
          isMagicDesignLayer: item?.isMagicDesignLayer,
          isInitializedNormalizeMask: item.isInitializedNormalizeMask ?? true,
          isInitializedNormalizedImage: item.isInitializedNormalizedImage ?? true,
          keyFrames: item.keyFrames
          // direction: 1, shadow: { color: 'blue', offsetX:20,offsetY:20,blur:30 }
        }
        if(item.type !== ObjectType.BAZAART_SHAP) {
          const now = new Date().toISOString()
          baseOptions = {
            ...baseOptions,
            //TODO: Hardcoded the default value
            // @ts-ignore
            isLiftBackgroundLayer: item.isLiftBackgroundLayer ? item.isLiftBackgroundLayer : false,
            audioVolume: item.audioVolume ?? 0,
            currentPrice: item.currentPrice ?? 0,
            features: item.features ?? [],
            hasFaceFeatures: item.hasFaceFeatures ?? false,
            hasPeople: item.hasPeople ?? 0,
            imagesFormat: item.imagesFormat ?? {},
            imagesLastUpdated: item.imagesLastUpdated ?? {},
            imagesOrientation: item.imagesOrientation ?? {},
            adjustments: item.adjustments ?? {},
            imageWidth: item.imageWidth ?? 0,
            isDeleted: item.isDeleted ?? false,
            isDirty: item.isDirty ?? false,
            isHidden: item.isHidden ?? false,
            isInitializing: item.isInitializing ?? false,
            isLocked: item.isLocked ?? false,
            isMagicBackgroundLayer: item.isMagicBackgroundLayer ?? false,
            isPlaceholder: item.isPlaceholder ?? false,
            isShopable: item.isShopable ?? false,
            isTemplateLayer: item.isTemplateLayer ?? false,
            itemType: item.type,
            lastPrice: item.lastPrice ?? 0,
            shouldUseMattedImage: item.shouldUseMattedImage ?? false,
            sourceGraphicsType: item.sourceGraphicsType ?? "Photos",
            state: item.state ?? 0,
            storeId: item.storeId ?? 0,
            thumbnailHeight: item.thumbnailHeight ?? 0,
            thumbnailWidth: item.thumbnailWidth ?? 0,
            version: item.version ?? 1,
            videoSpeed: item.videoSpeed ?? 1,
            zLayer: item.zLayer ?? 0
          }
        }
        
        const element = new fabric.StaticImage(img, baseOptions)
        const degrees = (item.angle || item.angle === 0) ? item.angle : fabric.util.radiansToDegrees(item.absoluteRotation);
        
        let rotationPointX = element.left - nonSymmetricOffset.x * imageScaleX;
        let rotationPointY = element.top - nonSymmetricOffset.y * imageScaleY;
        this.rotateAroundPointInsideObject(element, rotationPointX, rotationPointY, degrees)
        
        const { top, left } = element

        if (isNaN(top) || isNaN(left)) {
          element.set({
            top: options.top,
            left: options.left,
          })
          element.scaleToWidth(480)
        }

        if (item.blending) {
          element.globalCompositeOperation = blendMap[item.blending]
        }

        if (latestImageUrl && thumbnailImageUrl) {
          loadImageFromURL(latestImageUrl).then(async (img: any) => {
            console.log('latest Loaded')
            //@ts-ignore
            element.setElement(img)

            let imageWidth = item.sizeOnCanvas.width * options.width
            let imageHeight = (imageWidth / img.width) * img.height

            let thickness = item.effects?.outline?.thickness;
            if (thickness) {
              let adjustedSize = thickness * Math.max(imageWidth, imageHeight);
              imageWidth += adjustedSize;
              imageHeight += adjustedSize;
            }
        
        
            // let imageScaleX = item.scaleX ? item.scaleX : imageWidth / img.width
            // let imageScaleY = item.scaleY ? item.scaleY : imageHeight / img.height

            // element.scaleX = imageScaleX
            // element.scaleY = imageScaleY
            
            element.set({
              width: img.width,
              height: img.height,
              scaleX: imageWidth / img.width,
              scaleY: imageHeight / img.height,
              // @ts-ignore
              _originalScaleX: imageWidth / img.width,
              _originalScaleY: imageHeight / img.height,
            })
            element.setCoords()
          }).catch(function(err){
              console.error('failed to load asset with error:', err);
          })
        }

        resolve(element)
      } catch (err) {
        reject(err)
      }
    })
  }

  calculateFontSizeToFitWidth(text, fontFamily, targetWidth, tolerance = 1) {
      // Set an initial font size
      const initialFontSize = 10;
      const calcMaxLineWidth = (line: string): number => {
        // Use fabric.Text for single-line and fabric.Textbox for multi-line
        const tempTextObject = new fabric.Text(line, {
          fontFamily: fontFamily,
          fontSize: initialFontSize
        })
  
        // Measure the width at the initial font size
        const initialWidth = tempTextObject.width;
  
        // Calculate the scaling factor to fit the target width
        const scaleFactor = targetWidth / initialWidth;
        const calculatedFontSize = initialFontSize * scaleFactor / 1.01;
        return calculatedFontSize;
      }
      
      const safeText = text ?? '';
      let lines = safeText.split('\n');
      
      let linesWidth = lines.map(l => calcMaxLineWidth(l))
      let minLineFontWidth = Math.min(...linesWidth);
      
      return minLineFontWidth;
  }

  private extractTextStroke(item: any, maxEdge: number): {strokeColor?: string, strokeWidth?: number} {
    if (!item.effects.outline) {
      return {strokeColor: null, strokeWidth: null};
    }
    let retStroke = {
        strokeColor: item.stroke ? item.stroke : item.effects.outline.color,
        strokeWidth: item.strokeWidth ? item.strokeWidth : item.effects.outline.thickness * maxEdge
    }
    return retStroke;
  }

  private extractTextShadow(item: any, maxEdge: number): fabric.Shadow | null {
    if (!item.effects.shadow) {
      return null;
    }
    let shadowProps = item.effects.shadow
    const colorNumber = new PIXI.Color(shadowProps.color).toUint8RgbArray()
    let retShadow = item.shadow 
      ? item.shadow
      : {
          color: `rgba(${colorNumber[0]}, ${colorNumber[1]}, ${colorNumber[2]}, ${shadowProps.opacity})`,
          distance: shadowProps.distance,
          angle: shadowProps.angle,
          offsetX: Math.cos(shadowProps.angle) * shadowProps.distance * maxEdge,
          offsetY: Math.sin(shadowProps.angle) * shadowProps.distance * maxEdge,
          blur: Math.floor(shadowProps.blur * maxEdge),
        }
    return retShadow;
  }

  private extractLineSpacingRatio(item: any, textAttr: any): number { 
      let lineHeightInPixels = textAttr.NSParagraphStyle.NSMinLineHeight;
      if (!lineHeightInPixels) {
        return 1;
      }
      let originalFontSize = textAttr.NSOriginalFont?.size ?? textAttr.NSFont?.size
      let lineSpacingRatio = lineHeightInPixels / originalFontSize;
      return lineSpacingRatio
  }

  private extractLetterSpacing(item: any, textAttr: any): number {
    if (!textAttr.NSKern) {
      return 0;
    }
    let fontSize = textAttr.NSOriginalFont?.size ?? textAttr.NSFont?.size;
    let letterSpacing = (textAttr.NSKern / fontSize)
    return letterSpacing;
  }

  private extractTextColors(item: any, textAttr: any): {fill: string | null, backgroundColor: string | null }{
    let fill = textAttr.NSColor;
    let backgroundColor = null;

    if(item.backgroundColor){
      backgroundColor = item.backgroundColor
    }else if(item.textProperties.presentBackground){
      backgroundColor = item.textProperties.textColorHex 
    }
    if(backgroundColor){
      fill = isColorBright(backgroundColor) ? lightTheme.colors.blackGray : '#ffffff'
    }

    if (item.effects.kOverlayColorObject) {
      fill = fill ?? item.effects.kOverlayColorObject.color;
    }
    return {fill: fill, backgroundColor: backgroundColor }
  }

  private extractFontSize(item: any, textAttr: any, textOptions: any, itemWidth: number): number {
    let calcFontSize = 10;
    let fontRelativeToWidth = item.textProperties.imageToTextTransformation?.fontRelativeToWidth
    if (fontRelativeToWidth && fontRelativeToWidth !== -1) {
      let widthAdditional =item.textProperties.imageToTextTransformation?.sizeRelativeToWidth.width
      calcFontSize = itemWidth / (1 + (widthAdditional - 1) /2) * fontRelativeToWidth
      return Math.floor(calcFontSize);
    }

    if (!item.textProperties.imageToTextTransformation || item.textProperties.imageToTextTransformation.fontRelativeToWidth === -1) {
      let fontSize = this.calculateFontSizeToFitWidth(textOptions.text, textOptions.fontFamily, itemWidth);
      return fontSize
    }
    
    return calcFontSize;
  }

  private extractLineHeight(fontLineHeight: number, textAttr: any): number {
    let presetLineHeight = fabric.Text.prototype._fontSizeMult;
    if (textAttr.NSParagraphStyle.NSMinLineHeight) {
      let fontSize = textAttr.NSOriginalFont?.size ?? textAttr.NSFont?.size;
      let lineAddition = textAttr.NSParagraphStyle.NSMinLineHeight / fontSize / presetLineHeight;
      return lineAddition;
    } else  {
      return 1 / presetLineHeight
    }
  }

  [ObjectType.BAZAART_TEXT](item, options, mediaRepository) {
    return new Promise(async (resolve, reject) => {
      try {
        let actual_w = item.sizeOnCanvas.width * options.width
      
        let actual_h = actual_w / (item.sizeOnCanvas.width / item.sizeOnCanvas.height)
  
        const optionsAPI = this.getOptionsAPI(item,options)

        let baseOptions: any = {
          ...optionsAPI,
          angle: (item.angle || item.angle === 0) ? item.angle : fabric.util.radiansToDegrees(item.absoluteRotation),
          width: item.arcAngle ? actual_w : item.width ? item.width : actual_w,
          height: item.arcAngle ? actual_h : item.height ? item.height : actual_h,
          originX: 'center',
          originY: 'center',
          scaleX: 1,
          scaleY: 1,
          fill: lightTheme.colors.blackGray,
          metadata: {},
          flipX: item.flipX ? item.flipX : item.transformation.horizontalFlip,
          flipY: item.flipY ? item.flipY : item.transformation.verticalFlip,
          transformation:item.transformation,
          opacity: item.opacity ? item.opacity : 1,
          type: item.type,
          id: item.id ? item.id : nanoid(),
          bazaartGuid: item.bazaartGuid,
          selected: false,
          features:item?.features,
          paintFirst: 'stroke',
          fontStyle: 'normal',
          strokeLineJoin: 'round',
          strokeLineCap: 'round',
          itemType: item.itemType ? item.itemType : item.type,
          isResized: !!item.isResized,
          splitByGrapheme: !!item.splitByGrapheme,
          keyFrames: item.keyFrames
        }

        const textAttr = item.textProperties.attributedText.runs[0].attributes
        const textAlign = item.textAlign
        ? item.textAlign
        : textAttr.NSParagraphStyle.NSAlignment
              ? textAlignsNSFormat[textAttr.NSParagraphStyle.NSAlignment]
              : textAligns[item.textProperties.alignment]
        const textOptions = {
          ...baseOptions,
          text: item.text
            ? item.text.trim()
            : item.textProperties.attributedText.string
            ? item.textProperties.attributedText.string.trim()
            : 'Default Text',
          textProperties: item.textProperties,
          fontFamily: item.fontFamily ? item.fontFamily : textAttr.NSFont.systemName,
          fill: item.fill ? item.fill : textAttr.NSColor,
          textAlign: textAlign ?? 'center', // default to center
          sizeOnCanvas: item.sizeOnCanvas,
          transformation: item.transformation,
          effects: item.effects,
        }

        textOptions.arcAngle = item.arcAngle ? item.arcAngle : item.textProperties.arcAngle
        
        const charSpacing = this.extractLetterSpacing(item, textAttr);
        
        let originalFontSize = textAttr.NSOriginalFont?.size ?? textAttr.NSFont?.size
        let suggestedFont = MediaFontRepository.getInstance().get(textAttr.NSOriginalFont?.systemName ?? textAttr.NSFont.systemName);
        let fontInfo = suggestedFont ?? MediaFontRepository.getInstance().defaultFont;
        textOptions.lineHeight = 1.0;

        if (textAttr.NSParagraphStyle.NSMaxLineHeight) {
            const originalLineHeight = measureLineInfoForFont(fontInfo, originalFontSize).lineHeight;
            textOptions.lineHeight = textAttr.NSParagraphStyle.NSMaxLineHeight / originalLineHeight;
        }

        let { numOfLines, fontSize, lineHeight: actualLineHeightNoSpacing } = getFontSizeToMatchRect(fontInfo, textOptions.text, actual_w, actual_h, {
          lineSpacingRatio: textOptions.lineHeight,
          charSpacing: charSpacing,
          startFontSize: originalFontSize,
          arcAngle: textOptions.arcAngle
        })

        textOptions.fontSize = fontSize;
        textOptions.fontHeight = measureLineInfoForFont(fontInfo, fontSize).lineHeight;

        textOptions.fontToHeightRatio = item.textProperties.fontToHeightRatio ?? -1;
        textOptions.charSpacing = charSpacing * 1000;

        let maxEdge = Math.max(actual_w, actual_h);
        textOptions.shadow = this.extractTextShadow(item, maxEdge);
        let {strokeColor, strokeWidth} = this.extractTextStroke(item, maxEdge);
        textOptions.stroke = strokeColor;
        textOptions.strokeWidth = strokeWidth;
        
        let {fill, backgroundColor} = this.extractTextColors(item, textAttr);
        textOptions.fill = fill;
        textOptions.backgroundColor = backgroundColor;
        
        textOptions.top = Math.floor(item.top ? item.top : options.top + options.height * (item.centerPoint.y));
        textOptions.left = Math.floor(item.left ? item.left : options.left + options.width * (item.centerPoint.x));
        textOptions.originalFontSize = originalFontSize;
        
        const element = new fabric.OpenTypeText(textOptions)
        
        const { top, left, width, height } = element

        element.setFontInfo(fontInfo, element.fontFamily);
        
        let measure = measureTextMetrics
        let measurement = measure(element.textLines[0], fontInfo, element.fontSize, element.charSpacing / 1000, 0, true)
        element.top -= (measurement.baselineOffset + measurement.y1) / 2;
        
        // @ts-ignore
        element._originalHeight = element.height
        // @ts-ignore
        element._originalWidth = element.width
            
        element.setCoords() 

        if (isNaN(top) || isNaN(left)) {
          element.set({
            top: options.top + options.height / 2 - height / 2,
            left: options.left + options.width / 2 - width / 2,
          })
        }
        
        resolve(element)
      } catch (err) {
        reject(err)
      }
    })
  }

  [ObjectType.BAZAART_BG](item, options, mediaRepository) {
    return new Promise(async (resolve, reject) => {
      try {
        let baseOptions: any
        let image: any = null


        let latestImageUrl = await mediaRepository.getImage(
          item.bazaartGuid,
          item.layerAssetStateId,
          MediaImageType.latest
        )
        let thumbnailImageUrl = item.loadThumbnail === false ? null : await mediaRepository.getImage(
          item.bazaartGuid,
          item.layerAssetStateId,
          MediaImageType.thumbnail
        )

        if (item.version > 32) {
          image = await loadImageFromURL(thumbnailImageUrl ? thumbnailImageUrl : latestImageUrl)
        }

        let imageTargetWidthOnCanvas = options.width
        let imageTargetHeightOnCanvas = options.height;
        
        let imageScaleX = item.version > 32 ? item.scaleX ? item.scaleX : imageTargetWidthOnCanvas / image.width : 1;
        let imageScaleY = item.version > 32 ? item.scaleY ? item.scaleY : imageTargetHeightOnCanvas / image.height : 1;
        
        const topOpt = item.top ? item.top : options.top + options.height * item.centerPoint.y
        const leftOpt = item.left ? item.left : options.left + options.width * item.centerPoint.x
        const optionsAPI = this.getOptionsAPI(item,options)

        baseOptions = {
          ...optionsAPI,
          angle: (item.angle || item.angle === 0) ? item.angle : 0,
          top: topOpt,
          left: leftOpt,
          width: item.version > 32 ? image.width : options.width,
          height: item.version > 32 ? image.height : options.height,
          originX: 'center',
          originY: 'center',
          scaleX: imageScaleX,
          scaleY: imageScaleY,
          flipX: false,
          flipY: false,
          skewX: 0,
          skewY: 0,
          effects: item.effects ?? {hasFillImage: false},
          transformation: item.transformation ?? {
            horizontalFlip: false,
            verticalFlip: false,
          },
          filters: item.filters ? item.filters : [],
          metadata: {},
          selectable: false,
          hasControls: false,
          lockMovementY: true,
          lockMovementX: true,
          strokeWidth: 0,
          padding: 0,
          evented: false,
          type: ObjectType.BAZAART_BG,
          id: item.bazaartGuid,
          backgroundIdentifier:item?.backgroundIdentifier,
          layerAssetStateId: item.layerAssetStateId,
          isAnimated:item?.isAnimated,
          isBackgroundImage: item?.isBackgroundImage,
          startTime:item?.startTime,
          _endTime: item?._endTime,
          shouldUseMattedImage: item?.shouldUseMattedImage,
          version: item.version,
          videoMaskBoundingBox: item.videoMaskBoundingBox,
          videoMaskType: item.videoMaskType,
          keyFrames: item.keyFrames
        }

        const element = new fabric.StaticImage(image, {
          ...baseOptions,
          cropX: 0,
          cropY: 0,
          boundingBox: item.boundingBox
        })  

        const { top, left } = element

        if (isNaN(top) || isNaN(left)) {
          element.set({
            top: options.top,
            left: options.left,
          })
          element.scaleToWidth(480)
        }

        if (!thumbnailImageUrl) {
          resolve(element)
          return
        }

        setTimeout(() => {
          loadImageFromURL(latestImageUrl).then((image: any) => {
            //@ts-ignore
            element.setElement(image)
            
            let imageScaleX = item.version > 32 ? item.scaleX ? item.scaleX : imageTargetWidthOnCanvas / image.width : 1;
            let imageScaleY = item.version > 32 ? item.scaleY ? item.scaleY : imageTargetHeightOnCanvas / image.height : 1;

            element.set({
              width: item.version > 32 ? image.width : options.width,
              height: item.version > 32 ? image.height : options.height,
              scaleX: imageScaleX,
              scaleY: imageScaleY,
              // @ts-ignore
              _originalScaleX: imageScaleX,
              _originalScaleY: imageScaleY,
            })
          })
        }, 0);
        resolve(element)
      } catch (err) {
        reject(err)
      }
    })
  }

  [ObjectType.BAZAART_OVERLAY](item, options, mediaRepository) {
    return new Promise(async (resolve, reject) => {

      try {
        let baseOptions: any
        let image: any = null
        let latestImageUrl = await mediaRepository.getImage(
          item.bazaartGuid,
          item.layerAssetStateId,
          MediaImageType.latest
        )
        let thumbnailImageUrl = await mediaRepository.getImage(
          item.bazaartGuid,
          item.layerAssetStateId,
          MediaImageType.thumbnail
        )
        if (item.version <= 32) {
          resolve(null)
          return
        }

        image = await loadImageFromURL(thumbnailImageUrl ? thumbnailImageUrl : latestImageUrl)

        // Calculate target visual size on canvas
        // If we have saved scale and original image dimensions, compute target size from those
        // Otherwise, default to filling the canvas
        let targetVisualWidth: number
        let targetVisualHeight: number
        
        if (item.scaleX && item.imageWidth) {
          // Saved scale was for original image dimensions - compute target visual size
          targetVisualWidth = item.scaleX * item.imageWidth
          targetVisualHeight = item.scaleY * item.imageHeight
        } else {
          // Default to filling canvas
          targetVisualWidth = options.width
          targetVisualHeight = options.height
        }
        
        // Scale the current image (thumbnail or latest) to achieve the target visual size
        let imageScaleX = item.version > 32 ? targetVisualWidth / image.width : 1;
        let imageScaleY = item.version > 32 ? targetVisualHeight / image.height : 1;
        
        // For overlays, ALWAYS compute position from centerPoint/center - do NOT trust item.top/item.left
        // as they can have stale/incorrect values from cloning or transformations during export
        const centerX = item.centerPoint?.x ?? item.center?.x ?? 0.5
        const centerY = item.centerPoint?.y ?? item.center?.y ?? 0.5
        const topOpt = options.top + options.height * centerY
        const leftOpt = options.left + options.width * centerX


        const optionsAPI = this.getOptionsAPI(item,options)

        baseOptions = {
          ...optionsAPI,
          angle: (item.angle || item.angle === 0) ? item.angle : 0,
          top: topOpt,
          left: leftOpt,
          width: image.width,
          height: image.height,
          originX: 'center',
          originY: 'center',
          scaleX: imageScaleX,
          scaleY: imageScaleY,
          flipX: false,
          flipY: false,
          skewX: 0,
          skewY: 0,
          metadata: {},
          selectable: false,
          hasControls: false,
          lockMovementY: true,
          lockMovementX: true,
          strokeWidth: 0,
          padding: 0,
          evented: false,
          type: ObjectType.BAZAART_OVERLAY,
          id: item.bazaartGuid,
          layerAssetStateId: item.layerAssetStateId,
          blending:item?.blending,
          isAnimated:item?.isAnimated,
          overlayType:item?.overlayType,
          startTime:item?.startTime,
          _endTime:item?._endTime,
          sizeOnCanvas: item.sizeOnCanvas,
          keyFrames: item.keyFrames
        }

        const element = new fabric.StaticImage(image, {
          ...baseOptions,
          boundingBox: item.boundingBox,
          blending: item.blending,
          version: item.version,
          globalCompositeOperation: item.blending ? blendMap[item.blending] : blendMap.screenBlendMode,
          cropX: 0,
          cropY: 0,
        })

        const { top, left } = element
        if (isNaN(top) || isNaN(left)) {
          element.set({
            top: options.top,
            left: options.left,
          })
          element.scaleToWidth(480)
        }
        if (thumbnailImageUrl) {
          loadImageFromURL(latestImageUrl).then((img: any) => {
            console.log('latest Loaded')
            element.setElement(img)
            
            // Scale latest image to achieve the same target visual size
            const latestScaleX = targetVisualWidth / img.width
            const latestScaleY = targetVisualHeight / img.height
            
            element.set({
              width: img.width,
              height: img.height,
              scaleX: latestScaleX,
              scaleY: latestScaleY,
              // @ts-ignore
              _originalScaleX: latestScaleX,
              _originalScaleY: latestScaleY,
            })
          })
        }
        resolve(element)
      } catch (err) {
        reject(err)
      }
    })
  }

  [ObjectType.STATIC_PATH](item, options, mediaRepository) {
    return new Promise(async (resolve, reject) => {
      try {
        const baseOptions = this.getBaseOptions(item, options)
        const path = item.metadata.value
        const fill = item.metadata.fill
        const element = new fabric.StaticPath({ ...baseOptions, path, fill: fill ? fill : lightTheme.colors.blackGray })

        const { top, left } = element

        if (isNaN(top) || isNaN(left)) {
          element.set({
            top: options.top,
            left: options.left,
          })
          element.scaleToWidth(320)
        }
        resolve(element)
      } catch (err) {
        reject(err)
      }
    })
  }

  [ObjectType.STATIC_VECTOR](item, options, mediaRepository) {
    return new Promise(async (resolve, reject) => {
      try {
        const baseOptions = this.getBaseOptions(item, options)
        const src = item.metadata.src
        fabric.loadSVGFromURL(src, (objects, opts) => {
          const { width, height, top, left } = baseOptions
          if (!width || !height) {
            baseOptions.width = opts.width
            baseOptions.height = opts.height
            baseOptions.top = options.top
            baseOptions.left = options.left
          }
          const object = new fabric.StaticVector(objects, opts, { ...baseOptions, src })
          if (isNaN(top) || isNaN(left)) {
            object.set({
              top: options.top,
              left: options.left,
            })
            object.scaleToWidth(320)
          }
          resolve(object)
        })
      } catch (err) {
        reject(err)
      }
    })
  }

  [ObjectType.GROUP](item, options, mediaRepository){
    return new Promise(async (resolve, reject) => {
      try {
      
        const baseOptions = (({ left, top, width, height, scaleX, scaleY,angle, opacity, flipX, flipY, skewX, skewY}) =>
         ({ left, top, width, height, scaleX, scaleY,angle, opacity, flipX, flipY, skewX, skewY}))(item);

        let objects: fabric.Object[] = []
        for (const object of item.objects) {
          object.loadThumbnail = item.loadThumbnail
          objects = objects.concat(await this.run(object, options, mediaRepository))
        }
        // @ts-ignore
        const element = new fabric.Group(objects, baseOptions)
        resolve(element)
      } catch (err) {
        reject(err)
      }
    })
  }

  [ObjectType.BAZAART_IMAGE_FROM_TEXT](item, options, mediaRepository) {
    return new Promise(async (resolve, reject) => {
      try {
        let textImage = await mediaRepository.getImage(item.bazaartGuid, item.layerAssetStateId, MediaImageType.latest);
        const img: any = await loadImageFromURL(textImage)

        console.log('textImage: ', textImage);
        const optionsAPI = this.getOptionsAPI(item,options)
        let actual_w = img.width
        let actual_h = img.height
        
        const textAttr = item.textProperties.attributedText.runs[0].attributes
        const textAlign = item.textAlign
          ? item.textAlign
          : textAttr.NSParagraphStyle.NSAlignment
                ? textAlignsNSFormat[textAttr.NSParagraphStyle.NSAlignment]
                : textAligns[item.textProperties.alignment]
        const charSpacing = this.extractLetterSpacing(item, textAttr);
        const lineSpacingRatio = this.extractLineSpacingRatio(item, textAttr);
        
        const originalFontSize = textAttr.NSOriginalFont?.size ?? textAttr.NSFont?.size
        let suggestedFontInfo = MediaFontRepository.getInstance().get(textAttr.NSOriginalFont?.systemName ?? textAttr.NSFont.systemName);
        let fontInfo = suggestedFontInfo ?? MediaFontRepository.getInstance().defaultFont;
        const textContent = item.text
          ? item.text.trim()
          : item.textProperties.attributedText.string
          ? item.textProperties.attributedText.string.trim()
          : 'Default Text'
        const { numOfLines, fontSize, lineHeight: actualLineHeightNoSpacing } = getFontSizeToMatchRect(fontInfo, textContent, actual_w, actual_h, {
          lineSpacingRatio: lineSpacingRatio,
          charSpacing: charSpacing,
          startFontSize: originalFontSize
        })
        const maxEdge = Math.max(actual_w, actual_h);
        const {strokeColor, strokeWidth} = this.extractTextStroke(item, maxEdge);
        const {fill, backgroundColor} = this.extractTextColors(item, textAttr);
        const lineHeight = lineSpacingRatio !== 1 && numOfLines === 1 
          ? lineSpacingRatio 
          : lineSpacingRatio !== 1 
            ? (actual_h - actualLineHeightNoSpacing) / (numOfLines - 1) / actualLineHeightNoSpacing
            : 1.13;
        const textLines = textContent.split('\n');
        let baseOptions: any = {
          ...optionsAPI,
          type: ObjectType.BAZAART_IMAGE_FROM_TEXT,
          top: Math.floor(item.top ? item.top : options.top + options.height * (item.centerPoint.y)),
          left: Math.floor(item.left ? item.left : options.left + options.width * (item.centerPoint.x)),
          angle: (item.angle || item.angle === 0) ? item.angle : fabric.util.radiansToDegrees(item.absoluteRotation),
          originX: 'center',
          originY: 'center',
          paintFirst: 'stroke',
          fontStyle: 'normal',
          strokeLineJoin: 'round',
          strokeLineCap: 'round',
          isResized: !!item.isResized,
          metadata: {},
          flipX: item.flipX ? item.flipX : item.transformation.horizontalFlip,
          flipY: item.flipY ? item.flipY : item.transformation.verticalFlip,
          opacity: item.opacity ? item.opacity : 1,
          // @ts-ignore
          type: item.type,
          id: item.id ? item.id : nanoid(),
          bazaartGuid: item.bazaartGuid,
          effects: item.effects,
          sizeOnCanvas: item.sizeOnCanvas,
          selected: false,
          features: item?.features,
          itemType: item.itemType ? item.itemType : item.type,
          fill: fill,
          backgroundColor: backgroundColor,
          // This is the metadata for text conversion/export
          textMetadata: {
            text: textContent,
            textProperties: item.textProperties,
            fontFamily: item.fontFamily ? item.fontFamily : textAttr.NSFont.systemName,
            textAlign: textAlign ?? 'center', // default to center
            transformation: item.transformation,
            arcAngle: item.arcAngle ? item.arcAngle : item.textProperties.arcAngle,
            shadow: this.extractTextShadow(item, maxEdge),
            stroke: strokeColor,
            strokeWidth: strokeWidth,
            fontSize,
            charSpacing: charSpacing * 1000,
            lineHeight,
            textLines,
            splitByGrapheme: !!item.splitByGrapheme,
          },
        }

        if(!textImage) {
          textImage = await mediaRepository.getImage(item.bazaartGuid, item.layerAssetStateId, MediaImageType.latest);
          resolve(null)
        }
        const element = new fabric.ImageFromText(img, baseOptions)
          

        let scale = item.sizeOnCanvas.width * options.width / actual_w
        element.set({
          scaleX: scale,
          scaleY: scale,
        })
        element.setCoords()
        console.log('element: ', element);
        
        resolve(element)
      } catch (err) {
        reject(err)
      }
    })
  }

  getBaseOptions(item, options) {
    const { left, top, width, height, scaleX, scaleY } = item
    let metadata = item.metadata ? item.metadata : {}
    const { fill, angle, originX, originY } = metadata
    let baseOptions = {
      angle: angle ? angle : 0,
      top: options.top + top,
      left: options.left + left,
      width: width,
      height: height,
      originX: originX || 'center',
      originY: originY || 'center',
      scaleX: scaleX || 1,
      scaleY: scaleY || 1,
      fill: fill || lightTheme.colors.blackGray,
      metadata: metadata,
    }

    return baseOptions
  }

  getOptionsAPI(item, options){
    let optionsAPI={}
    for (let prop of PROPERTIES_TO_EXPORT) {
        optionsAPI[prop] = item[prop];
    }
    return optionsAPI
  }
}

export default new ObjectToFabric()
